import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import { useWebSocket, useUser } from './WebSocketContext';
import '../styles/Sidebar.css';
import { useTranslation } from 'react-i18next';
import '../i18n';
import CertificationExpiredOverlay from './CertificationExpiredOverlay';
import SoftwareOutdatedOverlay from './SoftwareOutdatedOverlay';
import { useSnackbar } from '../Utils/SnackBarContext';
import { apiRequest as webApiRequest, apiBinaryRequest as webApiBinaryRequest } from '../Utils/apiClient';
import { fetchCollections } from '../Utils/collectionsApi';
import SidebarDrawer from './SidebarDrawer';
import SidebarToggleButton from './SidebarToggleButton';
import SidebarLanguageMenu from './SidebarLanguageMenu';
import CaseContextMenu from './CaseContextMenu';
import CaseRenameDialog from './CaseRenameDialog';
import CaseDuplicateDialog from './CaseDuplicateDialog';
import SaveTemplateDialog from './SaveTemplateDialog';
import CollectionCreateDialog from './CollectionCreateDialog';
import AddToCollectionDialog from './AddToCollectionDialog';
import CaseDeleteDialog from './CaseDeleteDialog';
import { useCaseDelete } from '../hooks/useCaseDelete';
import { useFolderRestrictions } from './useFolderRestrictions';
import StorageBrowser from './StorageBrowser';

const Sidebar = ({ currentCaseId }) => {
  const { subscribeToMessages, sendMessage, logout, isUserLoggedIn, isConnected } = useWebSocket();
  const { openSnackbar } = useSnackbar();
  const [cases, setCases] = useState([]);
  const [pendingCopyCases, setPendingCopyCases] = useState([]);
  const [highlightCaseId, setHighlightCaseId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(currentCaseId);
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [newEventsCount, setNewEventsCount] = useState(0);
  const [credits, setCredits] = useState(0);
  const [tier, setTier] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [certOverlayOpen, setCertOverlayOpen] = useState(false);
  const [softwareOutdatedOverlayOpen, setSoftwareOutdatedOverlayOpen] = useState(false);
  const [downloadLink, setDownloadLink] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [storageStatus, setStorageStatus] = useState({
    isLoading: true,
    totalBytes: 0,
    usedBytes: 0,
    freeBytes: 0,
    usedPercent: 0,
    freePercent: 0,
    restrictToUserFolder: false,
    error: ''
  });
  const [storageBrowserPath, setStorageBrowserPath] = useState('');
  const [storageDisplayPath, setStorageDisplayPath] = useState('/');
  const [storageFolders, setStorageFolders] = useState([]);
  const [storageFiles, setStorageFiles] = useState([]);
  const [storageBrowserLoading, setStorageBrowserLoading] = useState(false);
  const [storageBrowserError, setStorageBrowserError] = useState('');
  const [storageDialogOpen, setStorageDialogOpen] = useState(false);
  const [selectedStorageRoot, setSelectedStorageRoot] = useState('');
  const [storageDownloadJob, setStorageDownloadJob] = useState({
    active: false,
    filename: '',
    totalChunks: 0,
    receivedChunks: 0
  });
  const storageDownloadChunksRef = useRef([]);
  const casesRequestSeqRef = useRef(0);
  const highlightTimeoutRef = useRef(null);

  // Menu states
  const [languageMenuAnchorEl, setLanguageMenuAnchorEl] = useState(null);
  const [caseMenuAnchorEl, setCaseMenuAnchorEl] = useState(null);
  const [selectedCaseForMenu, setSelectedCaseForMenu] = useState(null);

  // Dialog states
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingCase, setRenamingCase] = useState(null);
  const [renamingCaseId, setRenamingCaseId] = useState(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyingCase, setCopyingCase] = useState(null);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [templateCase, setTemplateCase] = useState(null);

  // Collection states
  const [collections, setCollections] = useState([]);
  const [collectionCreateDialogOpen, setCollectionCreateDialogOpen] = useState(false);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [addToCollectionDialogOpen, setAddToCollectionDialogOpen] = useState(false);
  const [caseToAddToCollection, setCaseToAddToCollection] = useState(null);

  const itemsPerPage = 15;
  const navigate = useNavigate();
  const location = useLocation();

  const activeCaseId = useMemo(() => {
    if (currentCaseId) return currentCaseId;
    const match = matchPath('/case/:caseid', location.pathname);
    const id = match ? parseInt(match.params.caseid, 10) : null;
    console.log('Sidebar activeCaseId derived:', id, 'from path:', location.pathname);
    return id;
  }, [currentCaseId, location.pathname]);

  const apiRequest = webApiRequest;

  const getListOfCases = useCallback(async (query = '', page = currentPage, collectionId = selectedCollection) => {
    const requestId = casesRequestSeqRef.current + 1;
    casesRequestSeqRef.current = requestId;

    console.log('getListOfCases called with query:', query, 'page:', page, 'collection:', collectionId);
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) {
        params.set('search', query);
      }
      params.set('page', String(page || 1));
      if (collectionId !== null && collectionId !== undefined && collectionId !== '') {
        params.set('collection_id', String(collectionId));
      }

      const data = await apiRequest(`/api/v1/sidebar/projects?${params.toString()}`);
      if (casesRequestSeqRef.current !== requestId) {
        return;
      }

      setCases(Array.isArray(data.cases) ? data.cases : []);
      setTotalCount(Number(data.total_count) || 0);
      setNewEventsCount(Number(data.new_events_count) || 0);
      setConnectionError(false);
    } catch (error) {
      if (casesRequestSeqRef.current !== requestId) {
        return;
      }
      console.error('Failed to load projects list:', error);
      setConnectionError(true);
    } finally {
      if (casesRequestSeqRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [apiRequest, currentPage, selectedCollection]);

  const debounceTimeoutRef = useRef(null);
  const debouncedGetListOfCases = useCallback((query, page) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      void getListOfCases(query, page, selectedCollection);
    }, 500);
  }, [getListOfCases, selectedCollection]);

  // Use case delete hook for DRY approach
  const {
    deleteDialogOpen,
    caseToDelete,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDelete,
    isDeleting,
    handleDeleteResponse
  } = useCaseDelete({
    sendMessage,
    isConnected,
    onDeleteSuccess: (deletedCase) => {
      // Refresh the cases list after successful delete
      void getListOfCases(searchQuery, currentPage, selectedCollection);
    },
    navigateOnDelete: true,
    currentCaseId: activeCaseId
  });

  // Theme colors
  const primaryColor = '#5757b0';
  const hoverColor = '#7777d0';
  const selectedColor = '#4545a0';
  const lightPurple = '#f0f0ff';

  // Sidebar handlers
  const toggleDrawer = () => {
    setIsOpen(prevState => !prevState);
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const handleSidebarOpen = () => {
      setIsOpen(true);
    };

    const handleCollectionSelect = (event) => {
      const collectionId = event?.detail?.collectionId;
      if (!collectionId) return;
      setSelectedCollection(collectionId);
      setCurrentPage(1);
      setIsOpen(true);
    };

    window.addEventListener('dashboard:open-sidebar', handleSidebarOpen);
    window.addEventListener('dashboard:open-collection', handleCollectionSelect);
    return () => {
      window.removeEventListener('dashboard:open-sidebar', handleSidebarOpen);
      window.removeEventListener('dashboard:open-collection', handleCollectionSelect);
    };
  }, []);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Language menu handlers
  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchorEl(null);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    console.log('Language changed to:', lng);
  };

  // Case menu handlers
  const handleCaseMenuOpen = (event, caseData) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('Opening case menu for:', caseData);
    setCaseMenuAnchorEl(event.currentTarget);
    setSelectedCaseForMenu(caseData);
  };

  const handleCaseMenuClose = () => {
    console.log('Closing case menu');
    setCaseMenuAnchorEl(null);
    setSelectedCaseForMenu(null);
  };

  const handleRenameClick = () => {
    console.log('Rename clicked for case:', selectedCaseForMenu);
    if (selectedCaseForMenu) {
      setRenamingCase(selectedCaseForMenu);
      setRenameDialogOpen(true);
    }
  };

  const handleRenameDialogClose = () => {
    setRenameDialogOpen(false);
    setRenamingCase(null);
  };

  const handleCopyClick = () => {
    console.log('Copy clicked for case:', selectedCaseForMenu);
    if (selectedCaseForMenu) {
      setCopyingCase(selectedCaseForMenu);
      setCopyDialogOpen(true);
    }
  };

  const handleCopyDialogClose = () => {
    setCopyDialogOpen(false);
    setCopyingCase(null);
  };

  const handleSaveAsTemplateClick = () => {
    console.log('Save as template clicked for case:', selectedCaseForMenu);
    if (selectedCaseForMenu) {
      setTemplateCase(selectedCaseForMenu);
      setSaveTemplateDialogOpen(true);
    }
  };

  const handleSaveTemplateDialogClose = () => {
    setSaveTemplateDialogOpen(false);
    setTemplateCase(null);
  };

  // Collection handlers
  const handleCollectionCreateDialogOpen = () => {
    setCollectionCreateDialogOpen(true);
  };

  const handleCollectionCreateDialogClose = () => {
    setCollectionCreateDialogOpen(false);
  };

  const handleCollectionChange = (collectionId) => {
    setSelectedCollection(collectionId);
    console.log('Collection changed to:', collectionId);
    // Reset to first page when changing collection
    setCurrentPage(1);
  };

  const handleCollectionCreate = (collectionName) => {
    if (!sendMessage || !isConnected) {
      console.log('WebSocket not available or not connected');
      openSnackbar(t('collection.create_error_connection') || 'Connection error. Please try again.', 3000, 'error');
      return;
    }

    try {
      const message = {
        command: 'createCollection',
        title: collectionName
      };

      console.log('Sending WebSocket createCollection message:', message);
      sendMessage(message);
      console.log('Create collection message sent, waiting for server response');
    } catch (error) {
      console.error('Error creating collection:', error);
      openSnackbar(t('collection.create_error') || 'Failed to create collection', 3000, 'error');
    }
  };

  const getCollections = useCallback(async () => {
    console.log('Fetching collections...');
    setCollectionsLoading(true);
    try {
      const payload = await fetchCollections();
      setCollections(Array.isArray(payload.collections) ? payload.collections : []);
    } catch (error) {
      console.error('Failed to load collections:', error);
      setCollections([]);
    } finally {
      setCollectionsLoading(false);
    }
  }, []);

  const handleAddToCollection = (caseItem) => {
    setCaseToAddToCollection(caseItem);
    setAddToCollectionDialogOpen(true);
  };

  const handleAddToCollectionConfirm = (collectionId) => {
    if (!caseToAddToCollection || !collectionId || !sendMessage || !isConnected) {
      console.log('Missing data for adding to collection');
      return;
    }

    try {
      const message = {
        command: 'addProjectToCollection',
        project_id: caseToAddToCollection.id,
        collection_id: collectionId
      };

      console.log('Sending addProjectToCollection message:', message);
      sendMessage(message);

      // Close dialog and reset state
      setAddToCollectionDialogOpen(false);
      setCaseToAddToCollection(null);

      openSnackbar(t('collection.case_added') || 'Case added to collection successfully', 3000, 'success');
    } catch (error) {
      console.error('Error adding case to collection:', error);
      openSnackbar(t('collection.add_error') || 'Failed to add case to collection', 3000, 'error');
    }
  };

  const handleAddToCollectionCancel = () => {
    setAddToCollectionDialogOpen(false);
    setCaseToAddToCollection(null);
  };

  const handleDeleteClick = () => {
    if (selectedCaseForMenu) {
      openDeleteDialog(selectedCaseForMenu);
    }
  };

  const handleSaveTemplateSubmit = ({ templateName, templateDescription, version, caseId }) => {
    console.log('Save template submit called with:', { templateName, templateDescription, version, caseId });

    if (!templateCase || !templateName.trim()) {
      console.log('Missing template data');
      return;
    }

    if (!sendMessage || !isConnected) {
      console.log('WebSocket not available or not connected');
      openSnackbar(t('template.save_error_connection') || 'Connection error. Please try again.', 3000, 'error');
      return;
    }

    try {
      const message = {
        command: 'saveTemplate',
        template_title: templateName,
        template_description: templateDescription,
        version: version,
        case_id: templateCase.id
      };

      console.log('Sending WebSocket saveTemplate message:', message);

      sendMessage(message);
      console.log('Save template message sent, waiting for server response');
      openSnackbar(t('template.save_success') || 'Template saved successfully', 3000, 'success');
    } catch (error) {
      console.error('Error saving template:', error);
      openSnackbar(t('template.save_error') || 'Failed to save template', 3000, 'error');
    }
  };

  const handleCopySubmit = (newTitle, collectionId) => {
    console.log('Copy submit called with:', { copyingCase, newTitle, collectionId });

    if (!copyingCase) {
      console.log('No case selected for copy');
      return;
    }

    if (!sendMessage || !isConnected) {
      console.log('WebSocket not available or not connected');
      openSnackbar(t('sidebar.copy_case_error_connection') || 'Connection error. Please try again.', 3000, 'error');
      return;
    }

    try {
      const tempId = `temp_copy_${Date.now()}`;
      const nowIso = new Date().toISOString();
      setPendingCopyCases(prev => ([
        {
          id: tempId,
          title: newTitle,
          description: copyingCase?.description || '',
          goal: copyingCase?.goal || '',
          created_at: nowIso,
          last_visit: nowIso,
          hasNewEvents: 0,
          isPublic: copyingCase?.isPublic || 0,
          isPendingCopy: true,
          pendingSince: Date.now()
        },
        ...prev
      ]));
      triggerHighlight(tempId);
      const message = {
        command: 'copyProject',
        caseid: copyingCase.id,
        title: newTitle,
        collection_id: collectionId
      };

      console.log('Sending WebSocket copy message:', message);
      sendMessage(message);
      console.log('Copy message sent, waiting for server response');
      // Note: Success message and refresh will be handled in the message subscription
    } catch (error) {
      console.error('Error copying case:', error);
      openSnackbar(t('sidebar.copy_case_error') || 'Failed to duplicate case', 3000, 'error');
    }
  };

  const triggerHighlight = useCallback((caseId) => {
    if (!caseId) return;
    setHighlightCaseId(caseId);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightCaseId(null);
    }, 8000);
  }, []);

  const removeOldestPendingCopy = useCallback(() => {
    setPendingCopyCases(prev => {
      const index = prev.findIndex(caseData => caseData?.isPendingCopy);
      if (index === -1) return prev;
      return prev.filter((_, idx) => idx !== index);
    });
  }, []);

  const forceGlobalCaseRefresh = useCallback(() => {
    setSearchQuery('');
    setCurrentPage(1);
    setSelectedCollection(null);
    void getListOfCases('', 1, null);
    if (sendMessage && isConnected) {
      sendMessage({ command: 'configuration_check' });
    }
  }, [getListOfCases, isConnected, sendMessage]);

  const handleRenameSubmit = (newTitle) => {
    console.log('Rename submit called with:', { renamingCase, newTitle });

    if (!renamingCase) {
      console.log('No case selected for rename');
      return;
    }

    if (!sendMessage || !isConnected) {
      console.log('WebSocket not available or not connected');
      openSnackbar(t('sidebar.rename_error_connection') || 'Connection error. Please try again.', 3000, 'error');
      return;
    }

    try {
      const message = {
        command: 'changetitle',
        caseid: renamingCase.id,
        title: newTitle
      };

      console.log('Sending WebSocket message:', message);

      // Store the case ID being renamed so we can update it when the server responds
      setRenamingCaseId(renamingCase.id);

      sendMessage(message); console.log('Rename message sent, waiting for server response');
      openSnackbar(t('sidebar.rename_success') || 'Case renamed successfully', 3000, 'success');
    } catch (error) {
      console.error('Error renaming case:', error);
      openSnackbar(t('sidebar.rename_error') || 'Failed to rename case', 3000, 'error');
    }
  };

  const handleToggleCaseIngestionPause = useCallback((caseData) => {
    if (!caseData || caseData.id === undefined || caseData.id === null) {
      return;
    }

    if (String(caseData.ingestionStatus || '').toLowerCase() === 'pending' || caseData.ingestionToggleAllowed === false) {
      openSnackbar(
        t('sidebar.project_pending_toggle_error', 'Finish the project creation flow before changing ingestion state'),
        3000,
        'warning'
      );
      return;
    }

    if (!sendMessage || !isConnected) {
      openSnackbar(t('sidebar.project_pause_error', 'Failed to update project ingestion state'), 3000, 'error');
      return;
    }

    const nextPaused = !Boolean(caseData.ingestionPaused);
    setCases(prevCases =>
      prevCases.map(item =>
        item.id === caseData.id
          ? {
              ...item,
              ingestionPaused: nextPaused ? 1 : 0,
              ingestionStatus: nextPaused ? 'paused' : 'active',
              ingestionToggleAllowed: true,
            }
          : item
      )
    );

    sendMessage({
      command: 'toggleCaseIngestionPause',
      caseid: caseData.id,
      paused: nextPaused
    });
  }, [isConnected, openSnackbar, sendMessage, t]);

  // Retry function for failed connections
  const retryConnection = useCallback(() => {
    console.log('Retrying connection...');
    setConnectionError(false);
    void getListOfCases(searchQuery, currentPage, selectedCollection);
    if (sendMessage && isConnected) {
      sendMessage({ command: 'configuration_check' });
    }
  }, [currentPage, getListOfCases, isConnected, searchQuery, selectedCollection, sendMessage]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.dataset.storageDialogOpen = storageDialogOpen ? 'true' : 'false';
    }
    if (typeof window !== 'undefined') {
      window.__storageDialogOpen = storageDialogOpen;
      window.dispatchEvent(new CustomEvent('storage-dialog', { detail: { open: storageDialogOpen } }));
    }
  }, [storageDialogOpen]);

  const { isAdmin, isEdit, hasStorageRead, hasStorageWrite } = useUser();
  const canManageStorage = hasStorageRead;
  const canWriteStorage = hasStorageWrite;
  const canAccessGlobalSettings = useMemo(() => isAdmin || isEdit, [isAdmin, isEdit]);

  const storageStatusRequestSeqRef = useRef(0);
  const storageDirectoryRequestSeqRef = useRef(0);

  const requestStorageStatus = useCallback(async () => {
    if (!canManageStorage) {
      return;
    }
    const requestId = storageStatusRequestSeqRef.current + 1;
    storageStatusRequestSeqRef.current = requestId;
    setStorageStatus((prev) => ({
      ...prev,
      isLoading: true,
      error: ''
    }));
    try {
      const payload = await apiRequest('/api/v1/storage/status');
      if (storageStatusRequestSeqRef.current !== requestId) {
        return;
      }
      const restrictFlag = Boolean(payload?.restrict_to_user_folder ?? payload?.restrictToUserFolder);
      const totalBytes = Number(payload?.total_bytes ?? payload?.totalBytes ?? payload?.total ?? 0);
      const usedBytes = Number(payload?.used_bytes ?? payload?.usedBytes ?? payload?.used ?? 0);
      const freeBytes = Number(payload?.free_bytes ?? payload?.freeBytes ?? payload?.free ?? (totalBytes - usedBytes));
      const usedPercentRaw = payload?.used_percent ?? payload?.usedPercent ?? (totalBytes ? (usedBytes / totalBytes) * 100 : 0);
      const freePercentRaw = payload?.free_percent ?? payload?.freePercent ?? (100 - usedPercentRaw);

      setStorageStatus({
        isLoading: false,
        totalBytes,
        usedBytes,
        freeBytes,
        usedPercent: Math.min(100, Math.max(0, Number(usedPercentRaw)) || 0),
        freePercent: Math.min(100, Math.max(0, Number(freePercentRaw)) || 0),
        path: payload?.path || '',
        restrictToUserFolder: restrictFlag,
        allowedRoots: Array.isArray(payload?.allowedRoots) ? payload.allowedRoots : [],
        error: ''
      });
    } catch (error) {
      if (storageStatusRequestSeqRef.current !== requestId) {
        return;
      }
      setStorageStatus((prev) => ({
        ...prev,
        isLoading: false,
        error: error?.message || t('sidebar.storage_error', 'Unable to load storage status')
      }));
    }
  }, [apiRequest, canManageStorage, t]);
  const { restrictToUserFolder, allowedRoots: folderRestrictionRoots } = useFolderRestrictions(activeCaseId);
  const storageConfigRoot = useMemo(() => storageStatus?.path || '', [storageStatus]);

  // Merge allowedRoots from useFolderRestrictions and storage_status response
  const allowedRoots = useMemo(() => {
    const roots = Array.isArray(folderRestrictionRoots) && folderRestrictionRoots.length > 0
      ? [...folderRestrictionRoots]
      : [];
    const statusRoots = Array.isArray(storageStatus?.allowedRoots) ? storageStatus.allowedRoots : [];
    for (const r of statusRoots) {
      if (r && !roots.some(existing => existing.replace(/\\/g, '/').toLowerCase() === r.replace(/\\/g, '/').toLowerCase())) {
        roots.push(r);
      }
    }
    console.log('[StorageRoots] folderRestrictionRoots:', folderRestrictionRoots, 'statusRoots:', statusRoots, 'merged:', roots);
    return roots;
  }, [folderRestrictionRoots, storageStatus]);

  const storageAllowedRoot = useMemo(() => {
    if (!canManageStorage) return '';
    const restrictionFlag = restrictToUserFolder || storageStatus?.restrictToUserFolder;
    if (!restrictionFlag) return '';
    if (selectedStorageRoot && Array.isArray(allowedRoots) && allowedRoots.some(r => {
      const normSel = (selectedStorageRoot || '').replace(/\\/g, '/').toLowerCase();
      const normR = (r || '').replace(/\\/g, '/').toLowerCase();
      return normSel === normR;
    })) {
      return selectedStorageRoot;
    }
    if (Array.isArray(allowedRoots) && allowedRoots.length > 0) {
      return allowedRoots[0];
    }
    return storageConfigRoot || '';
  }, [allowedRoots, restrictToUserFolder, storageConfigRoot, canManageStorage, storageStatus, selectedStorageRoot]);
  const storageRestrictionEnabled = useMemo(() => {
    const restrictionFlag = restrictToUserFolder || storageStatus?.restrictToUserFolder;
    return Boolean(canManageStorage && restrictionFlag);
  }, [restrictToUserFolder, canManageStorage, storageStatus]);
  const isRestrictedFolderMode = useMemo(
    () => Boolean(restrictToUserFolder || storageStatus?.restrictToUserFolder),
    [restrictToUserFolder, storageStatus]
  );

  const normalizePathForCompare = useCallback((value) => {
    if (!value) return '';
    return value.replace(/\\/g, '/').replace(/\/+/g, '/').toLowerCase().replace(/\/$/, '');
  }, []);

  const normalizedAllowedRoot = useMemo(() => normalizePathForCompare(storageAllowedRoot), [normalizePathForCompare, storageAllowedRoot]);

  const isPathWithinAllowed = useCallback((path) => {
    if (!storageRestrictionEnabled) return false;
    if (path === '/' || path === '\\') return true;
    const normalized = normalizePathForCompare(path);
    if (!normalized) return false;
    if (normalized.startsWith('/')) return true;
    if (!/^[a-z]:/.test(normalized)) return true;
    if (!normalizedAllowedRoot) return true;
    return normalized === normalizedAllowedRoot || normalized.startsWith(`${normalizedAllowedRoot}/`);
  }, [normalizedAllowedRoot, normalizePathForCompare, storageRestrictionEnabled]);

  const toRestrictedStoragePath = useCallback((value) => {
    const raw = typeof value === 'string' ? value : '';
    if (!storageRestrictionEnabled) {
      return raw;
    }

    const normalized = raw.replace(/\\/g, '/').replace(/\/+/g, '/').trim();
    if (!normalized || normalized === '/') {
      return '';
    }

    const trimmedInput = normalized.replace(/^\/+|\/+$/g, '');
    const normalizedRootPath = normalizePathForCompare(storageAllowedRoot) || '';
    if (!normalizedRootPath) {
      return trimmedInput;
    }

    const lowerInput = trimmedInput.toLowerCase();
    const lowerRoot = normalizedRootPath.toLowerCase();
    if (lowerInput === lowerRoot) {
      return '';
    }
    if (lowerInput.startsWith(`${lowerRoot}/`)) {
      return trimmedInput.slice(normalizedRootPath.length + 1);
    }

    // Display-style paths ("/foo/bar") are already relative to the restricted root.
    if (normalized.startsWith('/')) {
      return `/${trimmedInput}`;
    }

    return trimmedInput;
  }, [normalizePathForCompare, storageAllowedRoot, storageRestrictionEnabled]);

  const toAbsoluteStoragePath = useCallback((displayPath) => {
    if (!storageRestrictionEnabled || !storageAllowedRoot) return displayPath;
    if (!displayPath || displayPath === '/') return storageAllowedRoot;
    if (/^[A-Za-z]:/.test(displayPath)) return displayPath;
    const relative = displayPath.replace(/^\/+/, '');
    if (!relative) return storageAllowedRoot;
    const sep = storageAllowedRoot.includes('\\') ? '\\' : '/';
    const trimmedRoot = storageAllowedRoot.replace(/[\\/]+$/, '');
    return `${trimmedRoot}${sep}${relative}`;
  }, [storageAllowedRoot, storageRestrictionEnabled]);

  const resetStorageBrowser = useCallback(() => {
    setStorageBrowserPath('');
    setStorageDisplayPath('/');
    setStorageFolders([]);
    setStorageFiles([]);
    setStorageBrowserLoading(false);
    setStorageBrowserError('');
  }, []);

  const requestStorageDirectory = useCallback(async (targetPath) => {
    if (!storageRestrictionEnabled || !storageAllowedRoot) {
      resetStorageBrowser();
      return;
    }
    const nextPath = targetPath || storageAllowedRoot;
    if (!nextPath) {
      await requestStorageStatus();
      return;
    }
    const commandPath = toAbsoluteStoragePath(toRestrictedStoragePath(nextPath));
    const requestId = storageDirectoryRequestSeqRef.current + 1;
    storageDirectoryRequestSeqRef.current = requestId;
    setStorageBrowserLoading(true);
    setStorageBrowserError('');
    try {
      const payload = await apiRequest(`/api/v1/storage/directory?path=${encodeURIComponent(commandPath)}`);
      if (storageDirectoryRequestSeqRef.current !== requestId) {
        return;
      }
      const incomingPath = payload?.currentPath || '';
      const displayPath = payload?.displayPath ?? incomingPath;
      const normalizedIncoming = normalizePathForCompare(incomingPath);
      if (normalizedAllowedRoot && normalizedIncoming && !normalizedIncoming.startsWith(normalizedAllowedRoot)) {
        setStorageBrowserLoading(false);
        setStorageBrowserError(t('sidebar.storage_error', 'Unable to load storage'));
        return;
      }
      const normalizedFiles = Array.isArray(payload?.files)
        ? payload.files
            .map((file) => {
              if (typeof file === 'string') {
                return { name: file, createdAt: '' };
              }
              if (file && typeof file === 'object') {
                return {
                  name: file.name || '',
                  createdAt: file.created_at || file.createdAt || ''
                };
              }
              return null;
            })
            .filter((f) => f && f.name)
        : [];
      setStorageFolders(Array.isArray(payload?.folders) ? payload.folders : []);
      setStorageFiles(normalizedFiles);
      setStorageBrowserPath(incomingPath || storageAllowedRoot);
      setStorageDisplayPath(displayPath || '/');
      setStorageBrowserLoading(false);
    } catch (error) {
      if (storageDirectoryRequestSeqRef.current !== requestId) {
        return;
      }
      setStorageBrowserError(error?.message || t('sidebar.storage_error', 'Unable to load storage'));
      setStorageFolders([]);
      setStorageFiles([]);
      setStorageBrowserLoading(false);
    }
  }, [apiRequest, normalizePathForCompare, normalizedAllowedRoot, requestStorageStatus, resetStorageBrowser, storageAllowedRoot, storageRestrictionEnabled, t, toAbsoluteStoragePath, toRestrictedStoragePath]);

  const handleStorageFolderClick = useCallback((folderName) => {
    if (!folderName || !storageAllowedRoot) return;
    const basePath = storageRestrictionEnabled ? (storageDisplayPath || '/') : (storageBrowserPath || storageAllowedRoot);
    const separator = basePath.includes('\\') ? '\\' : '/';
    const trimmedBase = basePath.endsWith(separator) ? basePath.slice(0, -1) : basePath;
    const nextPath = trimmedBase ? `${trimmedBase}${separator}${folderName}` : `${separator}${folderName}`;
    if (!isPathWithinAllowed(nextPath)) {
      void requestStorageDirectory(storageAllowedRoot);
      return;
    }
    void requestStorageDirectory(nextPath);
  }, [isPathWithinAllowed, requestStorageDirectory, storageAllowedRoot, storageBrowserPath, storageDisplayPath, storageRestrictionEnabled]);

  const handleStorageNavigateUp = useCallback(() => {
    if (storageRestrictionEnabled) {
      const normalizedDisplayPath = (storageDisplayPath || '/').replace(/\\/g, '/');
      const segments = normalizedDisplayPath.split('/').filter(Boolean);
      if (segments.length === 0) {
        setStorageBrowserError(t('sidebar.storage_root_stop', 'You are already at the top of your allowed folder'));
        return;
      }
      segments.pop();
      const parentPath = segments.length ? `/${segments.join('/')}` : '/';
      void requestStorageDirectory(parentPath);
      return;
    }

    if (!storageBrowserPath) return;
    const segments = storageBrowserPath.split(/[\\/]/).filter(Boolean);
    if (segments.length === 0) return;
    const isWindows = storageBrowserPath.includes('\\');
    const separator = isWindows ? '\\' : '/';
    const hasDrive = isWindows && /^[A-Za-z]:/.test(segments[0]);
    let drive = '';
    let parts = segments;
    if (hasDrive) {
      drive = segments[0];
      parts = segments.slice(1);
    }
    if (parts.length === 0) {
      setStorageBrowserError(t('sidebar.storage_root_stop', 'You are already at the top of your allowed folder'));
      return;
    }
    parts.pop();
    const parentPath = hasDrive
      ? (parts.length ? `${drive}${separator}${parts.join(separator)}` : drive)
      : `${separator}${parts.join(separator)}`;
    if (!isPathWithinAllowed(parentPath)) {
      void requestStorageDirectory(storageAllowedRoot);
      return;
    }
    void requestStorageDirectory(parentPath);
  }, [isPathWithinAllowed, requestStorageDirectory, storageBrowserPath, storageAllowedRoot, storageDisplayPath, storageRestrictionEnabled, t]);

  const handleStorageRefresh = useCallback(() => {
    const currentPath = storageRestrictionEnabled
      ? (storageDisplayPath || '/')
      : (storageBrowserPath || storageAllowedRoot);
    if (currentPath) {
      void requestStorageDirectory(currentPath);
    }
  }, [requestStorageDirectory, storageAllowedRoot, storageBrowserPath, storageDisplayPath, storageRestrictionEnabled]);

  const openStorageDialog = useCallback(() => {
    if (storageRestrictionEnabled && !storageDialogOpen) {
      setStorageDialogOpen(true);
      if (!storageBrowserPath && storageAllowedRoot) {
        void requestStorageDirectory(storageAllowedRoot);
      }
    }
  }, [storageAllowedRoot, storageBrowserPath, storageDialogOpen, storageRestrictionEnabled, requestStorageDirectory]);

  const closeStorageDialog = useCallback(() => {
    setStorageDialogOpen(false);
  }, []);

  const handleStorageRootChange = useCallback((newRoot) => {
    if (!newRoot) return;
    setSelectedStorageRoot(newRoot);
  }, []);

  const prevStorageRootRef = React.useRef(storageAllowedRoot);
  useEffect(() => {
    if (storageAllowedRoot && storageAllowedRoot !== prevStorageRootRef.current && storageDialogOpen) {
      prevStorageRootRef.current = storageAllowedRoot;
      resetStorageBrowser();
      void requestStorageDirectory(storageAllowedRoot);
    }
    if (!prevStorageRootRef.current && storageAllowedRoot) {
      prevStorageRootRef.current = storageAllowedRoot;
    }
  }, [storageAllowedRoot, storageDialogOpen, resetStorageBrowser, requestStorageDirectory]);

  const extractDownloadFilename = useCallback((contentDisposition, fallbackName) => {
    const disposition = String(contentDisposition || '');
    if (!disposition) return fallbackName;

    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      try {
        return decodeURIComponent(utf8Match[1].replace(/"/g, '').trim()) || fallbackName;
      } catch (error) {
        return utf8Match[1].replace(/"/g, '').trim() || fallbackName;
      }
    }

    const quotedMatch = disposition.match(/filename="?([^";]+)"?/i);
    if (quotedMatch && quotedMatch[1]) {
      return quotedMatch[1].trim() || fallbackName;
    }

    return fallbackName;
  }, []);

  const triggerBlobDownload = useCallback((blob, fallbackName, contentDisposition) => {
    if (!blob) return;
    const filename = extractDownloadFilename(contentDisposition, fallbackName || 'download');
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [extractDownloadFilename]);

  const assembleAndSaveFile = useCallback((filename, chunks) => {
    if (!chunks || chunks.length === 0) return;
    const blob = new Blob(chunks, { type: 'application/octet-stream' });
    triggerBlobDownload(blob, filename || 'download');
  }, [triggerBlobDownload]);

  const handleStorageFileDownload = useCallback(async (fileName, fullPath) => {
    if (!canManageStorage || !storageRestrictionEnabled || !storageAllowedRoot || !fileName) {
      return;
    }

    const basePath = storageDisplayPath || '/';
    const separator = basePath.includes('\\') ? '\\' : '/';
    const trimmedBase = basePath.endsWith(separator) ? basePath.slice(0, -1) : basePath;
    const targetPath = fullPath || (trimmedBase ? `${trimmedBase}${separator}${fileName}` : `${separator}${fileName}`);
    const normalizedTargetPath = toAbsoluteStoragePath(targetPath);

    setStorageDownloadJob({
      active: true,
      filename: fileName,
      totalChunks: 0,
      receivedChunks: 0
    });

    try {
      const response = await webApiBinaryRequest(`/api/v1/storage/file?path=${encodeURIComponent(normalizedTargetPath)}`);
      const blob = await response.blob();
      triggerBlobDownload(blob, fileName, response.headers.get('Content-Disposition'));
      openSnackbar(t('sidebar.storage_download_success', 'File downloaded successfully'), 2000, 'success');
    } catch (error) {
      openSnackbar(error?.message || t('sidebar.storage_download_error', 'Failed to download file'), 3000, 'error');
    } finally {
      setStorageDownloadJob({
        active: false,
        filename: '',
        totalChunks: 0,
        receivedChunks: 0
      });
    }
  }, [canManageStorage, openSnackbar, storageAllowedRoot, storageDisplayPath, storageRestrictionEnabled, t, toAbsoluteStoragePath, triggerBlobDownload]);

  const handleStorageFolderDownload = useCallback(() => {
    if (!canManageStorage || !storageRestrictionEnabled || !storageAllowedRoot) {
      return;
    }
    const basePath = storageDisplayPath || '/';
    
    setStorageDownloadJob({
      active: true,
      preparing: true,
      filename: '',
      totalChunks: 0,
      receivedChunks: 0
    });

    const targetPath = toAbsoluteStoragePath(basePath);
    webApiBinaryRequest(`/api/v1/storage/download-folder?path=${encodeURIComponent(targetPath)}`)
      .then(async (response) => {
        const blob = await response.blob();
        const filename = extractDownloadFilename(response.headers.get('Content-Disposition'), 'storage_files.zip');
        triggerBlobDownload(blob, filename, response.headers.get('Content-Disposition'));
        openSnackbar(t('sidebar.storage_download_success', 'Folder downloaded successfully'), 2000, 'success');
      })
      .catch((error) => {
        openSnackbar(error?.message || t('sidebar.storage_download_error', 'Error downloading folder'), 3000, 'error');
      })
      .finally(() => {
        setStorageDownloadJob({
          active: false,
          preparing: false,
          filename: '',
          totalChunks: 0,
          receivedChunks: 0
        });
      });
  }, [canManageStorage, extractDownloadFilename, openSnackbar, storageAllowedRoot, storageDisplayPath, storageRestrictionEnabled, t, toAbsoluteStoragePath, triggerBlobDownload]);

  const handleStorageCreateFolder = useCallback(async (name) => {
    if (!canWriteStorage) {
      return;
    }
    if (!storageRestrictionEnabled) {
      openSnackbar(t('sidebar.storage_not_restricted', 'Storage actions are only available in restricted mode'), 3000, 'error');
      return;
    }
    if (!storageAllowedRoot) {
      openSnackbar(t('sidebar.storage_no_root', 'Storage location unavailable'), 3000, 'error');
      return;
    }
    const folderName = (name || '').trim();
    if (!folderName) {
      openSnackbar(t('sidebar.storage_folder_name_required', 'Folder name is required'), 3000, 'error');
      return;
    }
    const basePath = storageDisplayPath || '/';
    setStorageBrowserLoading(true);
    setStorageBrowserError('');
    try {
      await webApiRequest('/api/v1/storage/create-folder', {
        method: 'POST',
        body: JSON.stringify({
          path: toAbsoluteStoragePath(basePath),
          name: folderName
        })
      });
      openSnackbar(t('sidebar.storage_folder_created', 'Folder created'), 2000, 'success');
      handleStorageRefresh();
    } catch (error) {
      setStorageBrowserLoading(false);
      setStorageBrowserError(error?.message || t('sidebar.storage_error', 'Unable to load storage'));
      openSnackbar(error?.message || t('sidebar.storage_error', 'Unable to load storage'), 3000, 'error');
    }
  }, [canWriteStorage, handleStorageRefresh, openSnackbar, storageAllowedRoot, storageDisplayPath, storageRestrictionEnabled, t, toAbsoluteStoragePath]);

  const handleStorageDelete = useCallback(async (targetName, targetType) => {
    if (!canWriteStorage) {
      return;
    }
    if (!storageRestrictionEnabled) {
      openSnackbar(t('sidebar.storage_not_restricted', 'Storage actions are only available in restricted mode'), 3000, 'error');
      return;
    }
    if (!storageAllowedRoot) {
      openSnackbar(t('sidebar.storage_no_root', 'Storage location unavailable'), 3000, 'error');
      return;
    }
    if (!targetName) return;
    const basePath = storageDisplayPath || '/';
    const separator = basePath.includes('\\') ? '\\' : '/';
    const trimmedBase = basePath.endsWith(separator) ? basePath.slice(0, -1) : basePath;
    const targetPath = trimmedBase ? `${trimmedBase}${separator}${targetName}` : `${separator}${targetName}`;
    setStorageBrowserLoading(true);
    setStorageBrowserError('');
    try {
      await webApiRequest('/api/v1/storage/delete', {
        method: 'POST',
        body: JSON.stringify({
          path: toAbsoluteStoragePath(targetPath),
          targetType
        })
      });
      openSnackbar(t('sidebar.storage_deleted', 'Deleted successfully'), 2000, 'success');
      handleStorageRefresh();
    } catch (error) {
      setStorageBrowserLoading(false);
      setStorageBrowserError(error?.message || t('sidebar.storage_error', 'Unable to load storage'));
      openSnackbar(error?.message || t('sidebar.storage_error', 'Unable to load storage'), 3000, 'error');
    }
  }, [canWriteStorage, handleStorageRefresh, openSnackbar, storageAllowedRoot, storageDisplayPath, storageRestrictionEnabled, t, toAbsoluteStoragePath]);

  const handleStorageDeleteFile = useCallback((name) => handleStorageDelete(name, 'file'), [handleStorageDelete]);
  const handleStorageDeleteFolder = useCallback((name) => handleStorageDelete(name, 'folder'), [handleStorageDelete]);

  const handleStorageRename = useCallback(async (targetName, targetType, newName) => {
    if (!canWriteStorage) {
      return;
    }
    if (!storageRestrictionEnabled) {
      openSnackbar(t('sidebar.storage_not_restricted', 'Storage actions are only available in restricted mode'), 3000, 'error');
      return;
    }
    if (!storageAllowedRoot) {
      openSnackbar(t('sidebar.storage_no_root', 'Storage location unavailable'), 3000, 'error');
      return;
    }
    if (!targetName) return;
    const trimmed = (newName || '').trim();
    if (!trimmed) {
      openSnackbar(t('sidebar.storage_rename_required', 'New name is required'), 3000, 'error');
      return;
    }
    const basePath = storageDisplayPath || '/';
    const separator = basePath.includes('\\') ? '\\' : '/';
    const trimmedBase = basePath.endsWith(separator) ? basePath.slice(0, -1) : basePath;
    const targetPath = trimmedBase ? `${trimmedBase}${separator}${targetName}` : `${separator}${targetName}`;
    setStorageBrowserLoading(true);
    setStorageBrowserError('');
    try {
      await webApiRequest('/api/v1/storage/rename', {
        method: 'POST',
        body: JSON.stringify({
          path: toAbsoluteStoragePath(targetPath),
          newName: trimmed,
          targetType
        })
      });
      openSnackbar(t('sidebar.storage_renamed', 'Renamed successfully'), 2000, 'success');
      handleStorageRefresh();
    } catch (error) {
      setStorageBrowserLoading(false);
      setStorageBrowserError(error?.message || t('sidebar.storage_rename_error', 'Unable to rename'));
      openSnackbar(error?.message || t('sidebar.storage_rename_error', 'Unable to rename'), 3000, 'error');
    }
  }, [canWriteStorage, handleStorageRefresh, openSnackbar, storageAllowedRoot, storageDisplayPath, storageRestrictionEnabled, t, toAbsoluteStoragePath]);

  const handleStorageRenameFile = useCallback((name, newName) => handleStorageRename(name, 'file', newName), [handleStorageRename]);
  const handleStorageRenameFolder = useCallback((name, newName) => handleStorageRename(name, 'folder', newName), [handleStorageRename]);

  useEffect(() => {
    console.log('Sidebar effect triggered - sendMessage available:', !!sendMessage, 'isConnected:', isConnected);
    if (sendMessage && isConnected) {
      console.log('Getting list of cases...');
      // Check configuration when WebSocket is connected
      console.log('Sending configuration check...');
      sendMessage({ command: 'configuration_check' });
      setConnectionError(false);
    } else {
      console.log('sendMessage not available yet or WebSocket not connected');
      if (!isConnected) {
        setConnectionError(true);
      }
    }
  }, [isConnected, sendMessage]);

  useEffect(() => {
    void getCollections();
  }, [getCollections, isUserLoggedIn]);

  useEffect(() => {
    if (!canManageStorage) {
      setStorageStatus({
        isLoading: false,
        totalBytes: 0,
        usedBytes: 0,
        freeBytes: 0,
        usedPercent: 0,
        freePercent: 0,
        restrictToUserFolder: false,
        error: ''
      });
      resetStorageBrowser();
      return;
    }

    if (!isOpen) {
      return;
    }

    void requestStorageStatus();
  }, [canManageStorage, isOpen, requestStorageStatus, resetStorageBrowser]);

  useEffect(() => {
    if (storageRestrictionEnabled && storageAllowedRoot) {
      void requestStorageDirectory(storageAllowedRoot);
    } else {
      resetStorageBrowser();
    }
  }, [requestStorageDirectory, resetStorageBrowser, storageRestrictionEnabled, storageAllowedRoot]);

  useEffect(() => {
    console.log('Setting up message subscription...');
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      // console.log('Sidebar received message:', parsedMessage.command, parsedMessage);

      if (parsedMessage.command === "certification") {
        console.log("Status:", parsedMessage.data.status);
        if (parsedMessage.data.status === "software_outdate") {
          setDownloadLink(parsedMessage.data.download || '#');
          setSoftwareOutdatedOverlayOpen(true);
        }
        if (parsedMessage.data.status === "certification_invalid") {
          console.log("open certification expired overlay");
          setCertOverlayOpen(true);
        }
      } else if (parsedMessage.command === "configuration_data") {
        console.log('Sidebar received configuration_data tier:', parsedMessage.data?.tier, parsedMessage.data);
        // Check if a text model provider is selected (not just if token exists)
        const hasTextModel = parsedMessage.data && parsedMessage.data.modelTextHost && parsedMessage.data.modelTextHost.trim() !== '';
        if (!hasTextModel && canAccessGlobalSettings) {
          console.log("No text model selected. Redirecting to settings.");
          // Navigate to settings page if no model is selected
          // Don't redirect view_only users - they can't configure anyway
          navigate('/Settings');
        }

        // Update credits if available
        if (parsedMessage.data) {
          if (parsedMessage.data.credits !== undefined) {
            setCredits(parsedMessage.data.credits);
          }
          if (parsedMessage.data.tier !== undefined && parsedMessage.data.tier !== null) {
            setTier(String(parsedMessage.data.tier).trim());
          }
        }
      } else if (parsedMessage.command === "changetitle") {
        // Handle successful title change response from server
        console.log("Title changed successfully:", parsedMessage.data);
        // Update only the specific case in the existing list using the stored case ID
        const newTitle = parsedMessage.data;
        if (renamingCaseId) {
          setCases(prevCases =>
            prevCases.map(caseData =>
              caseData.id === renamingCaseId
                ? { ...caseData, title: newTitle }
                : caseData
            )
          );
          // Clear the renaming case ID
          setRenamingCaseId(null);
          console.log('Case title updated in list for case ID:', renamingCaseId);
        }
      } else if (parsedMessage.command === "copyProject") {
        // Handle copyProject response from server
        console.log("Copy project response:", parsedMessage);
        const payload = parsedMessage.data || parsedMessage;
        if (parsedMessage.status === "success" || payload?.status === "success") {
          openSnackbar(t('sidebar.copy_case_success') || 'Case duplicated successfully', 3000, 'success');
          const newCaseId = payload?.new_case_id || payload?.newCaseId;
          if (newCaseId) {
            setPendingCopyCases(prev => {
              let removed = false;
              return prev.filter(caseData => {
                if (!removed && caseData?.isPendingCopy && typeof caseData.id === 'string' && caseData.id.startsWith('temp_copy_')) {
                  removed = true;
                  return false;
                }
                return true;
              });
            });
            setSelectedCaseId(newCaseId);
            navigate(`/case/${newCaseId}`);
            triggerHighlight(newCaseId);
          }
          // Refresh the cases list to show the new copied case (page 1, no filters)
          forceGlobalCaseRefresh();
        } else {
          const errorMsg = parsedMessage.message || parsedMessage.data?.message || t('sidebar.copy_case_error') || 'Failed to duplicate case';
          openSnackbar(errorMsg, 3000, 'error');
          removeOldestPendingCopy();
        }
      } else if (parsedMessage.command === "deletecase") {
        // Handle deletecase response from server using the hook
        console.log("Delete case response received:", parsedMessage);

        let success = false;
        if (typeof parsedMessage.data === 'string') {
          success = parsedMessage.data === "success";
        } else if (parsedMessage.data && parsedMessage.data.value) {
          success = parsedMessage.data.value === "success";
        } else if (parsedMessage.data && parsedMessage.data.status) {
          success = parsedMessage.data.status === "success";
        }

        console.log("Delete success status:", success, "caseToDelete:", caseToDelete);

        handleDeleteResponse(success, caseToDelete?.id);
        if (success) {
          // Refresh the cases list
          debouncedGetListOfCases(searchQuery, currentPage);
        }
      } else if (parsedMessage.command === "saveTemplate") {
        // Handle saveTemplate response from server
        console.log("Save template response:", parsedMessage);
        if (parsedMessage.status === "success") {
          openSnackbar(parsedMessage.message || t('template.save_success') || 'Template saved successfully', 3000, 'success');
        } else {
          openSnackbar(parsedMessage.message || t('template.save_error') || 'Failed to save template', 3000, 'error');
        }
      } else if (parsedMessage.command === "createCollection") {
        // Handle createCollection response from server
        console.log("Create collection response:", parsedMessage);
        if (parsedMessage.data && parsedMessage.data.status === "success") {
          openSnackbar(parsedMessage.data.message || t('collection.create_success') || 'Collection created successfully', 3000, 'success');
          // Add the new collection to the list
          if (parsedMessage.data.collection) {
            setCollections(prevCollections => [...prevCollections, parsedMessage.data.collection]);
          }
          // Close the dialog
          setCollectionCreateDialogOpen(false);
          // Refresh collections list
          getCollections();
        } else {
          openSnackbar(parsedMessage.data?.message || t('collection.create_error') || 'Failed to create collection', 3000, 'error');
        }
      } else if (parsedMessage.command === "addProjectToCollection") {
        // Handle addProjectToCollection response from server
        console.log("Add project to collection response:", parsedMessage);
        if (parsedMessage.data && parsedMessage.data.status === "success") {
          openSnackbar(t('collection.case_added_success') || 'Case added to collection successfully', 3000, 'success');
          // Refresh the cases list to reflect the new collection assignment
          debouncedGetListOfCases(searchQuery, currentPage);
        } else {
          console.error('Error adding case to collection:', parsedMessage.data?.message);
          openSnackbar(t('collection.add_error') || 'Failed to add case to collection', 3000, 'error');
        }
      } else if (parsedMessage.command === "toggleCaseIngestionPause") {
        const payload = parsedMessage.data || {};
        if (payload.status === "error" || payload.error) {
          openSnackbar(
            payload.message || payload.error || t('sidebar.project_pause_error', 'Failed to update project ingestion state'),
            3000,
            'error'
          );
          debouncedGetListOfCases(searchQuery, currentPage);
        } else {
          const caseId = payload.caseid;
          const ingestionPaused = Boolean(payload.ingestionPaused);
          const ingestionStatus = String(payload.ingestionStatus || (ingestionPaused ? 'paused' : 'active')).toLowerCase();
          const ingestionToggleAllowed = payload.ingestionToggleAllowed !== false;
          setCases(prevCases =>
            prevCases.map(caseData =>
              caseData.id === caseId
                ? {
                    ...caseData,
                    ingestionPaused: ingestionPaused ? 1 : 0,
                    ingestionStatus,
                    ingestionToggleAllowed,
                  }
                : caseData
            )
          );
          openSnackbar(
            ingestionPaused
              ? t('sidebar.project_pause_success', 'Project ingestion paused')
              : t('sidebar.project_resume_success', 'Project ingestion resumed'),
            2000,
            'success'
          );
        }
      }

    });

    return unsubscribe;
  }, [subscribeToMessages, navigate, renamingCaseId, openSnackbar, t, getCollections, currentPage, debouncedGetListOfCases, searchQuery, handleDeleteResponse, caseToDelete?.id, canAccessGlobalSettings, canManageStorage, storageRestrictionEnabled, storageAllowedRoot, normalizedAllowedRoot, normalizePathForCompare, requestStorageDirectory, storageBrowserPath, storageDisplayPath, forceGlobalCaseRefresh, removeOldestPendingCopy, triggerHighlight]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      if (parsedMessage.command !== 'openfile') {
        return;
      }
      const data = parsedMessage.data || {};
      if (!storageDownloadJob.active) {
        return;
      }

      const status = data.status || '';
      if (status === 'file_stream_start') {
        setStorageDownloadJob((prev) => ({
          ...prev,
          totalChunks: data.total_chunks || 0,
          receivedChunks: 0
        }));
        storageDownloadChunksRef.current = [];
      } else if (status === 'file_stream_chunk') {
        if (!data.chunk_data) return;
        try {
          const binaryString = atob(data.chunk_data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          storageDownloadChunksRef.current[data.chunk_number || 0] = bytes;
          setStorageDownloadJob((prev) => ({
            ...prev,
            receivedChunks: prev.receivedChunks + 1
          }));
        } catch (e) {
          console.error('Error processing download chunk', e);
        }
      } else if (status === 'file_stream_complete') {
        const validChunks = storageDownloadChunksRef.current.filter(Boolean);
        assembleAndSaveFile(storageDownloadJob.filename || data.filename, validChunks);
        storageDownloadChunksRef.current = [];
        setStorageDownloadJob({
          active: false,
          filename: '',
          totalChunks: 0,
          receivedChunks: 0
        });
      } else if (status === 'error') {
        setStorageDownloadJob({
          active: false,
          filename: '',
          totalChunks: 0,
          receivedChunks: 0
        });
        openSnackbar(data.message || t('sidebar.storage_download_error', 'Failed to download file'), 3000, 'error');
      }
    });

    return unsubscribe;
  }, [assembleAndSaveFile, openSnackbar, storageDownloadJob.active, storageDownloadJob.filename, subscribeToMessages, t]);

  useEffect(() => {
    if (currentCaseId) {
      setSelectedCaseId(currentCaseId);
    }
  }, [currentCaseId]);

  const sortedCases = useMemo(() => {
    const caseMap = new Map();
    cases.forEach(caseData => {
      if (caseData && caseData.id !== undefined) {
        caseMap.set(caseData.id, caseData);
      }
    });

    const pending = pendingCopyCases.filter(caseData => {
      if (!caseData || caseData.id === undefined) {
        return false;
      }
      return !caseMap.has(caseData.id);
    });

    return [...pending, ...cases];
  }, [cases, pendingCopyCases]);

  useEffect(() => {
    if (!pendingCopyCases.length) {
      return;
    }
    setPendingCopyCases(prev => {
      const now = Date.now();
      const serverCases = cases || [];
      let changed = false;
      const result = [];

      prev.forEach(caseData => {
        if (!caseData?.isPendingCopy) {
          changed = true;
          return;
        }

        const existsInServer = serverCases.some(serverCase => String(serverCase.id) === String(caseData.id));
        if (existsInServer) {
          changed = true;
          return;
        }

        const hasTempId = typeof caseData.id === 'string' && caseData.id.startsWith('temp_copy_');
        if (hasTempId) {
          const pendingSince = caseData.pendingSince || now;
          const titleLower = (caseData.title || '').trim().toLowerCase();
          const match = serverCases.find(serverCase => {
            const serverTitle = (serverCase?.title || '').trim().toLowerCase();
            if (!titleLower || serverTitle !== titleLower) {
              return false;
            }
            const serverCreatedAt = serverCase?.created_at || serverCase?.createdAt || '';
            const createdTime = serverCreatedAt ? new Date(String(serverCreatedAt).replace(' ', 'T')).getTime() : NaN;
            return Number.isNaN(createdTime) || Math.abs(createdTime - pendingSince) < 10 * 60 * 1000;
          });

          if (match?.id) {
            triggerHighlight(match.id);
            changed = true;
            return;
          }
        }

        const pendingSince = caseData.pendingSince || now;
        if (now - pendingSince > 2 * 60 * 1000) {
          changed = true;
          return;
        }

        result.push(caseData);
      });

      return changed ? result : prev;
    });
  }, [cases, pendingCopyCases.length, triggerHighlight]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    debouncedGetListOfCases(searchQuery, currentPage);
  }, [searchQuery, currentPage, debouncedGetListOfCases]);

  const handleListItemClick = useCallback((id) => {
    setSelectedCaseId(id);

    setCases(prevCases =>
      prevCases.map(caseData =>
        caseData.id === id ? { ...caseData, hasNewEvents: 0 } : caseData
      )
    );

    setNewEventsCount(prevCount => {
      const selectedCase = cases.find(c => c.id === id);
      return selectedCase && selectedCase.hasNewEvents === 1
        ? prevCount - 1
        : prevCount;
    });

    // Trigger configuration check when a case is clicked
    if (sendMessage && isConnected) {
      sendMessage({ command: 'configuration_check' });
    }
  }, [cases, sendMessage, isConnected]);

  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;

  return (
    <>
      {/* Main Sidebar Drawer */}
      <SidebarDrawer
        isOpen={isOpen}
        onToggle={toggleDrawer}
        cases={sortedCases}
        highlightCaseId={highlightCaseId}
        collections={collections}
        collectionsLoading={collectionsLoading}
        selectedCollection={selectedCollection}
        onCollectionChange={handleCollectionChange}
        selectedCaseId={selectedCaseId}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onCaseClick={handleListItemClick}
        onCaseMenuOpen={handleCaseMenuOpen}
        onToggleCaseIngestionPause={handleToggleCaseIngestionPause}
        onLanguageMenuOpen={handleLanguageMenuOpen}
        onLogout={logout}
        isUserLoggedIn={isUserLoggedIn}
        isLoading={isLoading}
        connectionError={connectionError}
        onRetry={retryConnection}
        onCreateProject={() => navigate('/add')}
        onCreateCollection={handleCollectionCreateDialogOpen}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
        selectedColor={selectedColor}
        lightPurple={lightPurple}
        storageStatus={storageStatus}
        credits={credits}
        tier={tier}
        restrictToUserFolder={storageRestrictionEnabled}
        storageDisplayPath={storageDisplayPath}
        storageFolders={storageFolders}
        storageFiles={storageFiles}
        storageBrowserLoading={storageBrowserLoading}
        storageBrowserError={storageBrowserError}
        onStorageFolderClick={handleStorageFolderClick}
        onStorageNavigateUp={handleStorageNavigateUp}
        onStorageRefresh={handleStorageRefresh}
        onStorageOpen={openStorageDialog}
      />

      {/* Sidebar Toggle Button */}
      <SidebarToggleButton
        onClick={toggleDrawer}
        isOpen={isOpen}
        newEventsCount={newEventsCount}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
      />

      {/* Language Menu */}
      <SidebarLanguageMenu
        anchorEl={languageMenuAnchorEl}
        open={Boolean(languageMenuAnchorEl)}
        onClose={handleLanguageMenuClose}
        onLanguageChange={changeLanguage}
        primaryColor={primaryColor}
        lightPurple={lightPurple}
      />

      {/* Case Context Menu */}
      <CaseContextMenu
        anchorEl={caseMenuAnchorEl}
        open={Boolean(caseMenuAnchorEl)}
        onClose={handleCaseMenuClose}
        onRename={handleRenameClick}
        onCopy={handleCopyClick}
        onSaveAsTemplate={handleSaveAsTemplateClick}
        onAddToCollection={() => handleAddToCollection(selectedCaseForMenu)}
        onDelete={handleDeleteClick}
        isDeleting={isDeleting}
        primaryColor={primaryColor}
        lightPurple={lightPurple}
      />

      {/* Rename Dialog */}
      <CaseRenameDialog
        open={renameDialogOpen}
        onClose={handleRenameDialogClose}
        currentTitle={renamingCase?.title || ''}
        onRename={handleRenameSubmit}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
      />

      {/* Copy Dialog */}
      <CaseDuplicateDialog
        open={copyDialogOpen}
        onClose={handleCopyDialogClose}
        originalTitle={copyingCase?.title || ''}
        onDuplicate={handleCopySubmit}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
        collections={collections}
        defaultCollectionId={selectedCollection}
      />

      {/* Save Template Dialog */}
      <SaveTemplateDialog
        open={saveTemplateDialogOpen}
        onClose={handleSaveTemplateDialogClose}
        onSave={handleSaveTemplateSubmit}
        caseData={templateCase}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
      />

      {/* Collection Create Dialog */}
      <CollectionCreateDialog
        open={collectionCreateDialogOpen}
        onClose={handleCollectionCreateDialogClose}
        onCreate={handleCollectionCreate}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
      />

      {/* Add to Collection Dialog */}
      <AddToCollectionDialog
        open={addToCollectionDialogOpen}
        onClose={handleAddToCollectionCancel}
        onConfirm={handleAddToCollectionConfirm}
        onCancel={handleAddToCollectionCancel}
        collections={collections}
        loading={collectionsLoading}
        caseTitle={caseToAddToCollection?.title}
        primaryColor={primaryColor}
        lightPurple={lightPurple}
      />

      {/* Delete Confirmation Dialog - Using reusable component */}
      <CaseDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        caseTitle={caseToDelete?.title}
      />

      {/* Overlays */}
      {certOverlayOpen && (
        <CertificationExpiredOverlay
          open={certOverlayOpen}
          onClose={() => setCertOverlayOpen(false)}
        />
      )}
      {softwareOutdatedOverlayOpen && (
        <SoftwareOutdatedOverlay
          open={softwareOutdatedOverlayOpen}
          onClose={() => setSoftwareOutdatedOverlayOpen(false)}
          downloadLink={downloadLink}
          hideDownload={isRestrictedFolderMode}
        />
      )}

      <StorageBrowser
        open={storageDialogOpen}
        onClose={closeStorageDialog}
        displayPath={storageDisplayPath}
        folders={storageFolders}
        files={storageFiles}
        loading={storageBrowserLoading}
        error={storageBrowserError}
        onFolderClick={handleStorageFolderClick}
        onNavigateUp={handleStorageNavigateUp}
        onNavigate={requestStorageDirectory}
        onRefresh={handleStorageRefresh}
        onCreateFolder={handleStorageCreateFolder}
        onDeleteFile={handleStorageDeleteFile}
        onDeleteFolder={handleStorageDeleteFolder}
        onRenameFile={handleStorageRenameFile}
        onRenameFolder={handleStorageRenameFolder}
        onDownload={handleStorageFileDownload}
        onDownloadAll={handleStorageFolderDownload}
        currentPath={storageRestrictionEnabled ? (storageDisplayPath || '/') : (storageBrowserPath || storageAllowedRoot || '')}
        canManageStorage={canManageStorage}
        canWriteStorage={canWriteStorage}
        primaryColor={primaryColor}
        downloadingFolder={storageDownloadJob.preparing || false}
        restrictToUserFolder={storageRestrictionEnabled}
        allowedRoots={allowedRoots || []}
        activeRoot={storageAllowedRoot}
        onRootChange={handleStorageRootChange}
      />
    </>
  );
};

export default Sidebar;
